import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import * as Yup from "yup";
import { useFormik } from "formik";

import { getAppSettings, updateAppSettings } from "../../store/actions";
import {
  AppSettingsDto,
  UpdateAppSettingsRequest,
} from "../../interfaces/app-settings";
import { State } from "../../interfaces";
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  Label,
  Row,
} from "reactstrap";
import { AppSettingsTypeEnum } from "../../enums";
import { ActivityButton, AppRichEditor } from "../../components/Common";

export const OneSubjectBenefitsPage = () => {
  //#region hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  //#endregion

  document.title = `${t("documentTitle", {
    pageName: "controlAppOneSubjectBenefits",
  })}`;

  //#region internal state
  const [request] = useState<AppSettingsDto>();
  //#endregion

  //#region redux state
  const {
    loading,
    error,
    response: appSettings,
  } = useSelector(
    (state: any): State<AppSettingsDto> => state.appSettings.appSettings
  );

  const {
    loading: updating,
    success: updateSuccess,
    error: updateError,
  } = useSelector(
    (state: any): State<AppSettingsDto> => state.appSettings.updateAppSettings
  );
  //#endregion

  //#region form
  const form = useFormik<UpdateAppSettingsRequest>({
    enableReinitialize: true,
    initialValues: {
      value: {
        en: request?.value.en || "",
        ar: request?.value.ar || "",
      },
    },
    initialErrors: error || updateError,
    validationSchema: Yup.object({
      value: Yup.object({
        en: Yup.string().required(
          t("isNotEmpty", {
            displayName: "englishValue",
          })
        ),
        ar: Yup.string().required(
          t("isNotEmpty", {
            displayName: "arabicValue",
          })
        ),
      }),
    }),
    onSubmit: (values) => {
      dispatch(
        updateAppSettings(
          AppSettingsTypeEnum.PURCHASE_SINGLE_SUBJECT_DESCRIPTION,
          values,
          history
        )
      );
    },
  });
  //#endregion

  //#region effects

  useEffect(() => {
    dispatch(
      getAppSettings(
        AppSettingsTypeEnum.PURCHASE_SINGLE_SUBJECT_DESCRIPTION,
        history
      )
    );
  }, []);

  useEffect(() => {
    if (appSettings) {
      form.setValues({
        value: appSettings.value,
      });
    }
  }, [appSettings]);

  useEffect(() => {
    if (!updating && updateSuccess) {
      toast.success(t("savedSuccessfully"), {
        autoClose: 3000,
      });
      setTimeout(() => {
        dispatch(
          getAppSettings(
            AppSettingsTypeEnum.PURCHASE_SINGLE_SUBJECT_DESCRIPTION,
            history
          )
        );
      }, 1000);
    }
  }, [updating]);

  //#endregion

  //#region handlers

  //#endregion

  return (
    <Row>
      <Col>
        <Card>
          <CardHeader>
            <h6 className="card-title mb-0">{t("oneSubjectBenefits")}</h6>
          </CardHeader>
          <CardBody>
            <ToastContainer />
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
                return false;
              }}
              action="#"
            >
              {typeof error === "string" && (
                <Alert color="danger" className="rounded-0 mb-0">
                  <p className="mb-0">{error}</p>
                </Alert>
              )}
              {typeof updateError === "string" && (
                <Alert color="danger" className="rounded-0 mb-0">
                  <p className="mb-0">{updateError}</p>
                </Alert>
              )}
              <Row>
                <Col className="col-sm-12 col-md-12 col-lg-6 col-12">
                  <div className="mb-3">
                    <Label htmlFor="value.en" className="form-label">
                      {t("valueEn")}
                      <span className="text-danger">*</span>
                    </Label>
                    <AppRichEditor
                      initialValue={appSettings?.value?.en || ""}
                      onChange={(data: any) => {
                        form.setFieldValue("value.en", data);
                      }}
                      onKeyUp={(data: any) => {
                        if (data.trim().length == 0) {
                          form.setFieldError(
                            "value.en",
                            t("isNotEmpty", {
                              displayName: "englishValue",
                            })
                          );
                        } else {
                          form.setFieldError("value.en", "");
                        }
                      }}
                      onFocus={() => {
                        form.setFieldTouched("value.en", true, true);
                      }}
                    />
                    {form.touched.value?.en && form.errors.value?.en ? (
                      <Label className="invalid-feedback d-block">
                        {form.errors.value?.en as string}
                      </Label>
                    ) : null}
                  </div>
                </Col>
                <Col>
                  <div className="mb-3">
                    <Label htmlFor="value.ar" className="form-label">
                      {t("valueAr")}
                      <span className="text-danger">*</span>
                    </Label>
                    <AppRichEditor
                      initialValue={appSettings?.value?.ar || ""}
                      onChange={(data: any) => {
                        form.setFieldValue("value.ar", data);
                      }}
                      onKeyUp={(data: any) => {
                        if (data.trim().length == 0) {
                          form.setFieldError(
                            "value.ar",
                            t("isNotEmpty", {
                              displayName: "arabicValue",
                            })
                          );
                        } else {
                          form.setFieldError("value.ar", "");
                        }
                      }}
                      onFocus={() => {
                        form.setFieldTouched("value.ar", true, true);
                      }}
                    />
                    {form.touched.value?.ar && form.errors.value?.ar ? (
                      <Label className="invalid-feedback d-block">
                        {form.errors.value?.ar as string}
                      </Label>
                    ) : null}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "center",
                  }}
                >
                  <ActivityButton
                    title="save"
                    inProgressTitle="saving"
                    color="success"
                    className="btn btn-success"
                    type="submit"
                    inProgress={loading || updating}
                    disabled={loading || updating}
                  ></ActivityButton>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};
